import { Box } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";

import { CORE_PANEL_IDS } from "contexts/sidePanels";
import {
    ToolbarButtonEntry,
    toolbarsActions,
    useDispatchToolbars,
    useToolbar,
    useToolbarButtons,
} from "contexts/toolbars";
import { useIsPanelOpenUnified, useUnifiedPanelToggle } from "hooks/useUnifiedPanelToggle";
import ObjectTreeIcon from "media/icons/object-tree.svg?react";
import PropertiesIcon from "media/icons/properties.svg?react";

import { PROCORE_BOTTOM_ENTRY } from "../constants";
import { BottomToolbarButton } from "./BottomToolbarButton";

interface GeneralButtonRendererProps {
    toolbarId: string;
}

/**
 * Custom button renderer for general content buttons
 */
function GeneralButtonRenderer({ toolbarId }: GeneralButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);

    const isViewsMarkupsOpen = useIsPanelOpenUnified(CORE_PANEL_IDS.VIEWS_MARKUPS);
    const isAllItemsOpen = useIsPanelOpenUnified(CORE_PANEL_IDS.ALL_ITEMS);
    const isObjectsOpen = useIsPanelOpenUnified(CORE_PANEL_IDS.OBJECTS);
    const isPropertiesOpen = useIsPanelOpenUnified(CORE_PANEL_IDS.PROPERTIES);
    const isGroupsOpen = useIsPanelOpenUnified(CORE_PANEL_IDS.GROUPS);
    const isDeviationsOpen = useIsPanelOpenUnified(CORE_PANEL_IDS.DEVIATIONS);

    if (buttons.length === 0) {
        return null;
    }

    // Map panel IDs to open states
    const openStates: Record<string, boolean> = {
        [CORE_PANEL_IDS.VIEWS_MARKUPS]: isViewsMarkupsOpen,
        [CORE_PANEL_IDS.ALL_ITEMS]: isAllItemsOpen,
        [CORE_PANEL_IDS.OBJECTS]: isObjectsOpen,
        [CORE_PANEL_IDS.PROPERTIES]: isPropertiesOpen,
        [CORE_PANEL_IDS.GROUPS]: isGroupsOpen,
        [CORE_PANEL_IDS.DEVIATIONS]: isDeviationsOpen,
    };

    return (
        <Box display="flex" gap={`${PROCORE_BOTTOM_ENTRY.GAP}px`}>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (!state.visible) {
                    return null;
                }

                const isActive = openStates[config.id] ?? state.active;

                return (
                    <BottomToolbarButton
                        key={config.id}
                        icon={config.icon}
                        label={config.title || ""}
                        tooltip={config.tooltip?.toolName || config.title || ""}
                        active={isActive}
                        onClick={config.onClick}
                    />
                );
            })}
        </Box>
    );
}

/**
 * Content for the General tab - opens side panels or drawers based on screen aspect ratio
 */
export function BottomToolbarGeneralContent() {
    const toolbarDispatch = useDispatchToolbars();
    const { togglePanel } = useUnifiedPanelToggle();

    // Keep a ref so onClick closures always call the latest togglePanel without
    // needing to recreate the buttons array on every panelMode flip. This prevents
    // addToolbar from becoming a no-op on re-runs (it's add-only) which would leave
    // stale onClick handlers after sidePanel <-> drawer transitions.
    const togglePanelRef = useRef(togglePanel);
    togglePanelRef.current = togglePanel;

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            // Commented out because features are not available yet
            // {
            //     config: {
            //         id: CORE_PANEL_IDS.VIEWS_MARKUPS,
            //         icon: <ViewList />,
            //         onClick: () => togglePanelRef.current(CORE_PANEL_IDS.VIEWS_MARKUPS),
            //         title: "Views &...",
            //         tooltip: {
            //             toolName: "Views & Markups",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false, // Active state from panel state
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            // {
            //     config: {
            //         id: CORE_PANEL_IDS.ALL_ITEMS,
            //         icon: <PinDrop />,
            //         onClick: () => togglePanelRef.current(CORE_PANEL_IDS.ALL_ITEMS),
            //         title: "All Items",
            //         tooltip: {
            //             toolName: "All Items",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            {
                config: {
                    id: CORE_PANEL_IDS.OBJECTS,
                    icon: <ObjectTreeIcon />,
                    onClick: () => togglePanelRef.current(CORE_PANEL_IDS.OBJECTS),
                    title: "Objects",
                    tooltip: {
                        toolName: "Object Tree",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: CORE_PANEL_IDS.PROPERTIES,
                    icon: <PropertiesIcon />,
                    onClick: () => togglePanelRef.current(CORE_PANEL_IDS.PROPERTIES),
                    title: "Properties",
                    tooltip: {
                        toolName: "Object Properties",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            // Commented out because features are not available yet
            // {
            //     config: {
            //         id: CORE_PANEL_IDS.GROUPS,
            //         icon: <Layers />,
            //         onClick: () => togglePanelRef.current(CORE_PANEL_IDS.GROUPS),
            //         title: "Groups",
            //         tooltip: {
            //             toolName: "Groups",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            // {
            //     config: {
            //         id: CORE_PANEL_IDS.DEVIATIONS,
            //         icon: <Settings />,
            //         onClick: () => togglePanelRef.current(CORE_PANEL_IDS.DEVIATIONS),
            //         title: "Deviations",
            //         tooltip: {
            //             toolName: "Deviations",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const toolbarId = "bottom-toolbar-general";
    const toolbarExists = useToolbar(toolbarId) !== undefined;

    // Ref guard: updated synchronously inside the effect so it's already true
    // when React Strict Mode re-runs effects in the same commit (before the
    // context state update from the first dispatch has re-rendered the component).
    // toolbarExists handles the case where the component remounts with a fresh ref
    // but the ToolbarProvider state (and SDK-added buttons) has persisted.
    const toolbarDispatchedRef = useRef(false);

    useEffect(() => {
        if (toolbarDispatchedRef.current || toolbarExists) {
            return;
        }
        toolbarDispatchedRef.current = true;
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );
        // Intentionally no cleanup: preserves SDK-added buttons across re-renders
    }, [toolbarDispatch, buttons, toolbarExists]);

    return <GeneralButtonRenderer toolbarId={toolbarId} />;
}
